import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import DoctorFeedbackService from '../services/doctor-feedback.service';
import PropTypes from 'prop-types';

const LeaveFeedbackForm = ({ therapistId, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [canLeaveFeedback, setCanLeaveFeedback] = useState(false);
  const [reason, setReason] = useState('');
  const [existingFeedback, setExistingFeedback] = useState(null);

  useEffect(() => {
    checkIfCanLeaveFeedback();
  }, [therapistId]);

  const checkIfCanLeaveFeedback = async () => {
    try {
      const response = await DoctorFeedbackService.canLeaveFeedback(therapistId);
      setCanLeaveFeedback(response.can_leave_feedback);
      setReason(response.reason || '');
      setExistingFeedback(response.existing_feedback || null);
      
      if (response.existing_feedback) {
        setRating(response.existing_feedback.rating);
        setComment(response.existing_feedback.comment || '');
      }
    } catch (err) {
      console.error('Errore nella verifica se puoi lasciare feedback:', err);
      setError('Si è verificato un errore. Riprova più tardi.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Seleziona un punteggio prima di inviare');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = {
        therapist_id: therapistId,
        rating,
        comment
      };
      
      let response;
      
      if (existingFeedback) {
        response = await DoctorFeedbackService.updateFeedback(existingFeedback.id, data);
      } else {
        response = await DoctorFeedbackService.leaveFeedback(data);
      }
      
      if (onSuccess) {
        onSuccess(response.feedback);
      }
      
      // Reset form if it's a new feedback
      if (!existingFeedback) {
        setRating(0);
        setComment('');
      }
      
      // Update the existing feedback reference
      setExistingFeedback(response.feedback);
      
    } catch (err) {
      console.error('Errore nell\'invio del feedback:', err);
      setError(err.message || 'Si è verificato un errore. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!existingFeedback) return;
    
    if (!window.confirm('Sei sicuro di voler eliminare questo feedback?')) return;
    
    setLoading(true);
    try {
      await DoctorFeedbackService.deleteFeedback(existingFeedback.id);
      setExistingFeedback(null);
      setRating(0);
      setComment('');
      setCanLeaveFeedback(true);
      setReason('');
      
      if (onSuccess) {
        onSuccess(null);
      }
    } catch (err) {
      console.error('Errore nella cancellazione del feedback:', err);
      setError(err.message || 'Si è verificato un errore. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  if (!canLeaveFeedback && !existingFeedback) {
    return (
      <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
        <p className="text-yellow-700">{reason || 'Non puoi lasciare un feedback per questo dottore al momento.'}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-lg font-semibold mb-4">
        {existingFeedback ? 'Modifica il tuo feedback' : 'Lascia un feedback'}
      </h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Valutazione</label>
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-8 h-8 cursor-pointer ${
                  star <= (hoveredRating || rating) 
                    ? 'text-yellow-400 fill-yellow-400' 
                    : 'text-gray-300'
                }`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
              />
            ))}
          </div>
        </div>
        
        <div className="mb-4">
          <label htmlFor="comment" className="block text-sm font-medium mb-2">
            Commento (opzionale)
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={4}
            placeholder="Condividi la tua esperienza..."
          />
        </div>
        
        <div className="flex justify-between">
          {existingFeedback && (
            <button
              type="button"
              onClick={handleDeleteFeedback}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              disabled={loading}
            >
              Elimina
            </button>
          )}
          
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 ml-auto"
            disabled={loading}
          >
            {loading ? 'Invio in corso...' : (existingFeedback ? 'Aggiorna' : 'Invia')}
          </button>
        </div>
      </form>
    </div>
  );
};

LeaveFeedbackForm.propTypes = {
  therapistId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default LeaveFeedbackForm;

